import React, { useMemo } from "react";
import { Button, Checkbox, Divider, Modal, Tag } from "antd";
import { LinearProgress } from "@mui/material";

import styles from "./EquipmentAllocationTable.module.css";

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatArea = (value) => {
  const numericValue = toFiniteNumber(value);
  return numericValue === null ? "Not available" : `${numericValue.toLocaleString()} sq. ft`;
};

const formatPercent = (value) => {
  if (value === null) return "Not available";
  const roundedValue = Math.round(value * 10) / 10;
  return `${roundedValue.toLocaleString()}%`;
};

const formatPlow = (plow) => {
  if (!plow) return "Not assigned";
  const model = plow.plow_model || plow.plow_size || "Unknown model";
  const name = plow.plow_name || `Plow ${plow.plow_id}`;
  return `${model} - ${name}`;
};

const formatDoubleDown = (doubleDown) => {
  if (!doubleDown) return { label: "Not assigned", title: "Not assigned" };
  const label = doubleDown.doubleDown_name || `Double Down ${doubleDown.doubleDown_id}`;
  const details = [doubleDown.doubleDown_model, doubleDown.doubleDown_type_name].filter(Boolean);
  return {
    label,
    title: details.length > 0 ? `${label} · ${details.join(" · ")}` : label,
  };
};

const buildStaffAssignments = (loaders) => {
  const seenAssignments = new Set();
  const uniquePeople = new Set();
  const assignments = [];

  loaders.forEach((loader) => {
    (loader.assigned_staff || []).forEach((staff, index) => {
      const staffIdentity = staff.staff_id ?? `${staff.name || "unknown"}-${index}`;
      const assignmentIdentity = `${loader.vehicle_id}-${staffIdentity}`;
      if (seenAssignments.has(assignmentIdentity)) return;

      seenAssignments.add(assignmentIdentity);
      uniquePeople.add(staff.staff_id ?? staff.name ?? assignmentIdentity);
      assignments.push({
        ...staff,
        assignmentIdentity,
        vehicle_id: loader.vehicle_id,
        vehicle_name: loader.vehicle_name,
      });
    });
  });

  return { assignments, uniquePeopleCount: uniquePeople.size };
};

const statusColor = (status) => {
  if (status === "Confirmed") return "green";
  if (status === "Unconfirmed") return "gold";
  return "default";
};

const formatStaffLockExplanation = (lockedSites) => {
  if (lockedSites.length === 1) {
    return `Staff allocation is finalized for ${lockedSites[0].site_name || "this site"}. Unlock it from the site row to make changes.`;
  }
  if (lockedSites.length > 1) {
    return `Staff allocation is finalized for ${lockedSites[0].site_name || "one site"} and ${lockedSites.length - 1} additional ${lockedSites.length === 2 ? "site" : "sites"}. Unlock them from the site rows to make changes.`;
  }
  return null;
};

const SiteDetailsModal = ({
  open,
  site,
  isViewingActiveSetup,
  onClose,
  onVehicleConfirmationChange,
  onPlowConfirmationChange,
  onDownloadPdf,
  vehicles = [],
  onDeallocateStaff,
  onSwapStaff,
  staffMutationBusy = false,
  deallocatingStaffId = null,
  refreshWarning = "",
  setupName,
  readOnly = false,
}) => {
  const loaders = useMemo(() => site?.vehicles_lots || [], [site?.vehicles_lots]);
  const { assignments: staffAssignments, uniquePeopleCount } = useMemo(
    () => buildStaffAssignments(loaders),
    [loaders],
  );
  const vehiclesById = useMemo(
    () => new Map(vehicles.map((vehicle) => [String(vehicle.vehicle_id), vehicle])),
    [vehicles],
  );
  const staffLocksByVehicleId = useMemo(() => {
    const locks = new Map();
    const currentSiteLocked = site && !site.archived && site.staff_allocation_finalized;

    staffAssignments.forEach((staff) => {
      const vehicleKey = String(staff.vehicle_id);
      if (locks.has(vehicleKey)) return;

      const vehicle = vehiclesById.get(vehicleKey);
      const lockedSitesById = new Map();
      (vehicle?.allocated_sites || [])
        .filter((allocatedSite) => !allocatedSite.archived && allocatedSite.staff_allocation_finalized)
        .forEach((allocatedSite) => {
          const key = allocatedSite.site_id ?? allocatedSite.site_name;
          lockedSitesById.set(key, allocatedSite);
        });

      if (currentSiteLocked) {
        const key = site.site_id ?? site.site_name;
        lockedSitesById.set(key, {
          site_id: site.site_id,
          site_name: site.site_name,
        });
      }

      const lockedSites = [...lockedSitesById.values()];
      const vehicleMissing = !vehicle;
      locks.set(vehicleKey, {
        disabled: lockedSites.length > 0 || vehicleMissing,
        warning: formatStaffLockExplanation(lockedSites)
          || (vehicleMissing
            ? `Staff allocation lock status is unavailable for ${staff.vehicle_name || "this loader"}, so removal is disabled.`
            : null),
        vehicleName: staff.vehicle_name || "this loader",
      });
    });

    return locks;
  }, [site, staffAssignments, vehiclesById]);
  const staffLockWarnings = useMemo(
    () => [...staffLocksByVehicleId.entries()]
      .filter(([, lock]) => lock.warning)
      .map(([vehicleId, lock]) => ({ vehicleId, ...lock })),
    [staffLocksByVehicleId],
  );

  if (!site) {
    return null;
  }

  const totalCapacity = toFiniteNumber(site.parking_lots_sqft);
  const allocatedCapacity = toFiniteNumber(site.parking_lots_sqft_allocated);
  const hasCapacityData = totalCapacity !== null && allocatedCapacity !== null;
  const allocationPercentage = hasCapacityData && totalCapacity > 0
    ? (allocatedCapacity / totalCapacity) * 100
    : null;
  const progressValue = allocationPercentage === null
    ? 0
    : Math.min(Math.max(allocationPercentage, 0), 100);
  const remainingCapacity = hasCapacityData
    ? Math.max(totalCapacity - allocatedCapacity, 0)
    : null;
  const siteOverCapacity = hasCapacityData && allocatedCapacity > totalCapacity;
  const overallocatedLoaderCount = Math.max(toFiniteNumber(site.num_overallocated_loaders) || 0, 0);
  const subtitle = [site.client_name, site.region].filter(Boolean).join(" · ");
  const saltUsage = site.salt_done === true
    ? "Yes"
    : site.salt_done === false
      ? "No"
      : "Not available";
  const progressColor = siteOverCapacity
    ? "#b42318"
    : allocationPercentage !== null && allocationPercentage >= 100
      ? "#034832"
      : allocationPercentage !== null && allocationPercentage > 0
        ? "#cdaf5e"
        : "#aeb8b5";

  return (
    <Modal
      title={(
        <div className={styles.siteModalHeader}>
          <span className={styles.vehicleModalEyebrow}>Site Details</span>
          <div className={styles.siteModalTitleRow}>
            <div className={styles.siteModalTitleContent}>
              <h2 className={styles.vehicleModalTitle} title={site.site_name || "Unnamed Site"}>
                {site.site_name || "Unnamed Site"}
              </h2>
              {subtitle && (
                <p className={styles.vehicleModalSubtitle} title={subtitle}>{subtitle}</p>
              )}
            </div>
            <div className={styles.vehicleModalTags} aria-label="Site statuses">
              <Tag color={statusColor(site.contract_status)}>
                Contract: {site.contract_status || "Not available"}
              </Tag>
              {overallocatedLoaderCount > 0 && (
                <Tag color="orange">
                  {overallocatedLoaderCount} overallocated {overallocatedLoaderCount === 1 ? "loader" : "loaders"}
                </Tag>
              )}
              {siteOverCapacity && <Tag color="red">Site over capacity</Tag>}
            </div>
          </div>
        </div>
      )}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={960}
      className={styles.siteDetailsModal}
      destroyOnClose
    >
      <div className={styles.siteModalContent}>
        <section className={styles.vehicleModalSection} aria-labelledby="site-overview-heading">
          <h3 id="site-overview-heading" className={styles.vehicleModalSectionTitle}>Site Overview</h3>
          <dl className={styles.siteOverviewGrid}>
            <div className={styles.siteOverviewItem}>
              <dt>Client</dt>
              <dd title={site.client_name || "Not available"}>{site.client_name || "Not available"}</dd>
            </div>
            <div className={styles.siteOverviewItem}>
              <dt>Site</dt>
              <dd title={site.site_name || "Not available"}>{site.site_name || "Not available"}</dd>
            </div>
            <div className={styles.siteOverviewItem}>
              <dt>Address</dt>
              <dd title={site.address || "Not available"}>{site.address || "Not available"}</dd>
            </div>
            <div className={styles.siteOverviewItem}>
              <dt>Parking lots area</dt>
              <dd>{formatArea(site.parking_lots_sqft)}</dd>
            </div>
            <div className={styles.siteOverviewItem}>
              <dt>Walks area</dt>
              <dd>{formatArea(site.walkway_sqft)}</dd>
            </div>
            <div className={styles.siteOverviewItem}>
              <dt>Salt Usage</dt>
              <dd>{saltUsage}</dd>
            </div>
          </dl>
        </section>

        <Divider />

        <section className={styles.vehicleModalSection} aria-labelledby="site-capacity-heading">
          <div className={styles.vehicleModalSectionHeading}>
            <h3 id="site-capacity-heading" className={styles.vehicleModalSectionTitle}>Loader Capacity</h3>
            <span>Parking-lot coverage</span>
          </div>
          {overallocatedLoaderCount > 0 && (
            <p className={styles.vehicleModalWarning}>
              {overallocatedLoaderCount} {overallocatedLoaderCount === 1 ? "loader is" : "loaders are"} marked overallocated at this site.
            </p>
          )}
          {siteOverCapacity && (
            <p className={styles.siteModalDanger}>
              Allocated Site coverage exceeds the parking-lot area by {formatArea(allocatedCapacity - totalCapacity)}.
            </p>
          )}
          <div className={styles.vehicleCapacityProgress}>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              className={styles.vehicleCapacityProgressBar}
              aria-label="Parking-lot Site coverage"
              aria-valuetext={formatPercent(allocationPercentage)}
              sx={{
                backgroundColor: "#dfe5e3",
                "& .MuiLinearProgress-bar": { backgroundColor: progressColor },
              }}
            />
            <span>{formatPercent(allocationPercentage)}</span>
          </div>
          <div className={styles.vehicleCapacityStats}>
            <div className={styles.vehicleCapacityStat}>
              <span>Allocated Site coverage</span>
              <strong>{formatArea(allocatedCapacity)}</strong>
            </div>
            <div className={styles.vehicleCapacityStat}>
              <span>Remaining</span>
              <strong>{formatArea(remainingCapacity)}</strong>
            </div>
            <div className={styles.vehicleCapacityStat}>
              <span>Total area</span>
              <strong>{formatArea(totalCapacity)}</strong>
            </div>
          </div>
        </section>

        <Divider />

        <section className={styles.vehicleModalSection} aria-labelledby="loader-assignments-heading">
          <div className={styles.vehicleModalSectionHeading}>
            <h3 id="loader-assignments-heading" className={styles.vehicleModalSectionTitle}>Loader Assignments</h3>
            <span>{loaders.length} {loaders.length === 1 ? "loader" : "loaders"}</span>
          </div>
          {!isViewingActiveSetup && loaders.length > 0 && (
            <p className={styles.siteModalNotice}>
              Vehicle and plow confirmation can only be changed on the active setup.
            </p>
          )}
          {loaders.length > 0 ? (
            <div className={styles.siteModalTableWrapper}>
              <table className={styles.siteModalTable}>
                <thead>
                  <tr>
                    <th scope="col" className={styles.siteModalLoaderColumn}>Loader</th>
                    <th scope="col">Site coverage</th>
                    <th scope="col">Loader capacity used</th>
                    <th scope="col">Plow</th>
                    <th scope="col" className={styles.siteModalDoubleDownColumn}>Double Down</th>
                    <th scope="col">Vehicle confirmed</th>
                    <th scope="col">Plow confirmed</th>
                  </tr>
                </thead>
                <tbody>
                  {loaders.map((loader) => {
                    const doubleDown = formatDoubleDown(loader.assigned_doubleDown);
                    return (
                      <tr key={loader.vehicle_id}>
                        <td className={styles.siteModalLoaderColumn}>
                          <div className={styles.siteModalPrimaryCell}>
                            <strong title={loader.vehicle_name || "Unnamed loader"}>
                              {loader.vehicle_name || "Unnamed loader"}
                            </strong>
                            <span className={styles.siteModalSecondary} title={loader.vehicle_subtype || "Not available"}>
                              {loader.vehicle_subtype || "Not available"}
                            </span>
                            {loader.overallocated_to_site && <Tag color="orange">Overallocated</Tag>}
                          </div>
                        </td>
                        <td>{formatArea(loader.site_allocated_capacity)}</td>
                        <td>{formatArea(loader.allocated_capacity)}</td>
                        <td>
                          <span className={styles.siteModalEllipsis} title={formatPlow(loader.assigned_plow)}>
                            {formatPlow(loader.assigned_plow)}
                          </span>
                        </td>
                        <td className={styles.siteModalDoubleDownColumn}>
                          <span className={styles.siteModalEllipsis} title={doubleDown.title}>
                            {doubleDown.label}
                          </span>
                        </td>
                        <td className={styles.siteModalCheckboxCell}>
                          <Checkbox
                            checked={Boolean(loader.is_confirmed)}
                            disabled={!isViewingActiveSetup}
                            aria-label={`Confirm vehicle ${loader.vehicle_name || loader.vehicle_id}`}
                            onChange={(event) => onVehicleConfirmationChange(event.target.checked, loader.vehicle_id)}
                          />
                        </td>
                        <td className={styles.siteModalCheckboxCell}>
                          {loader.assigned_plow ? (
                            <Checkbox
                              checked={Boolean(loader.assigned_plow.is_confirmed)}
                              disabled={!isViewingActiveSetup}
                              aria-label={`Confirm plow for ${loader.vehicle_name || loader.vehicle_id}`}
                              onChange={(event) => onPlowConfirmationChange(
                                event.target.checked,
                                loader.vehicle_id,
                                loader.assigned_plow.plow_id,
                              )}
                            />
                          ) : (
                            <span className={styles.siteModalMuted}>Not assigned</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.vehicleModalEmptyState}>No loaders are assigned to this site.</p>
          )}
        </section>

        <Divider />

        <section className={styles.vehicleModalSection} aria-labelledby="assigned-staff-heading">
          <div className={styles.vehicleModalSectionHeading}>
            <h3 id="assigned-staff-heading" className={styles.vehicleModalSectionTitle}>Assigned Staff</h3>
            <span>
              {uniquePeopleCount} {uniquePeopleCount === 1 ? "person" : "people"} · {staffAssignments.length} loader {staffAssignments.length === 1 ? "assignment" : "assignments"}
            </span>
          </div>
          {refreshWarning && (
            <p className={styles.vehicleModalWarning}>{refreshWarning}</p>
          )}
          {!readOnly && staffLockWarnings.map((lock) => (
            <p key={lock.vehicleId} className={styles.vehicleModalWarning}>
              <strong>{lock.vehicleName}: </strong>
              {lock.warning}
            </p>
          ))}
          {staffAssignments.length > 0 ? (
            <div className={styles.siteModalTableWrapper}>
              <table className={`${styles.siteModalTable} ${styles.siteModalStaffTable}`}>
                <thead>
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col" className={styles.siteModalLoaderColumn}>Loader</th>
                    <th scope="col">Position</th>
                    <th scope="col">Rating</th>
                    <th scope="col">Status</th>
                    <th scope="col">City</th>
                    {!readOnly && (
                      <th scope="col" className={styles.siteModalStaffActionColumn}>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {staffAssignments.map((staff) => (
                    <tr key={staff.assignmentIdentity}>
                      <td><strong title={staff.name || "Not available"}>{staff.name || "Not available"}</strong></td>
                      <td className={styles.siteModalLoaderColumn}>
                        <span className={styles.siteModalEllipsis} title={staff.vehicle_name || "Not available"}>
                          {staff.vehicle_name || "Not available"}
                        </span>
                      </td>
                      <td>{staff.position || "Not available"}</td>
                      <td>{staff.staff_rating ?? "Not available"}</td>
                      <td>{staff.status || "Not available"}</td>
                      <td>{staff.city || "Not available"}</td>
                      {!readOnly && (
                        <td className={styles.siteModalStaffActionColumn}>
                          <div className={styles.siteModalStaffActions}>
                            <Button
                              danger
                              size="small"
                              aria-label={`Deallocate ${staff.name || "staff member"} from ${staff.vehicle_name || "this loader"}`}
                              disabled={staffLocksByVehicleId.get(String(staff.vehicle_id))?.disabled || deallocatingStaffId !== null || staffMutationBusy}
                              loading={deallocatingStaffId === staff.staff_id}
                              onClick={() => {
                                Modal.confirm({
                                  title: "Remove loader assignment?",
                                  content: `Remove ${staff.name || "this staff member"} from ${staff.vehicle_name || "this loader"} in ${setupName || "the viewed setup"}? Loader, site, plow, and Double Down allocations will remain.`,
                                  okText: "Remove assignment",
                                  okButtonProps: { danger: true },
                                  cancelText: "Cancel",
                                  onOk: () => onDeallocateStaff(staff.vehicle_id, staff.staff_id),
                                });
                              }}
                            >
                              Deallocate Staff
                            </Button>
                            <Button
                              size="small"
                              className={styles.siteModalSwapButton}
                              aria-label={`Swap ${staff.name || "staff member"} on ${staff.vehicle_name || "this loader"}`}
                              aria-describedby={staff.archived ? `staff-swap-unavailable-${staff.vehicle_id}-${staff.staff_id}` : undefined}
                              disabled={staff.archived || !onSwapStaff || staffMutationBusy || deallocatingStaffId !== null || staffLocksByVehicleId.get(String(staff.vehicle_id))?.disabled}
                              onClick={() => onSwapStaff(staff)}
                            >
                              Swap
                            </Button>
                            {staff.archived && (
                              <span
                                id={`staff-swap-unavailable-${staff.vehicle_id}-${staff.staff_id}`}
                                className={styles.siteModalSwapDisabledReason}
                              >
                                Archived staff cannot be swapped.
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className={styles.vehicleModalEmptyState}>
              No staff are assigned to this site&apos;s loaders.
            </p>
          )}
        </section>

        <div className={styles.siteModalActions}>
          <Button type="primary" onClick={() => onDownloadPdf(site)}>
            Download PDF
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export { buildStaffAssignments, formatArea, toFiniteNumber };
export default SiteDetailsModal;
